import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import MmsIcon from '@mui/icons-material/Mms';
import { useFormikContext } from 'formik';
import { capitalize, isEmpty } from 'lodash';

import CommunicationAttachmentTable from '~/components/communications/CommunicationAttachmentTable';
import CommunicationReminderContainer from '~/components/communications/CommunicationCard/CommunicationReminderContainer';
import {
  getAttachmentFileUrl,
  getSmsCharacterLimit,
  SMS_FORM_FORMIK_IDS,
} from '~/components/communications/SmsCommunicationCard/utils';
import { getNotRetrievedMediaCount, notRetrievedMediaLabel } from '~/components/communications/utils';
import Text from '~/components/core/TextComponents/Text';
import InlineIconButton from '~/components/InlineIconButton';
import TextFieldFormik from '~/components/TextFieldFormik';
import Avatar from '~/components/ui/atoms/Avatar';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type {
  CommunicationAttachment,
  ContactBaseModel,
  SmsCommunicationBase,
} from '~/server_shared/swagger/data-contracts';

// Define the SMS message interface for thread messages
interface SmsMessage extends SmsCommunicationBase {
  id: number;
  message: string;
  direction: 'Incoming' | 'Outgoing';
  datetime: string;
  attachments: CommunicationAttachment[];
  is_attachment_exist: boolean;
}

// Define the SMS communication interface with thread
interface SmsCommunicationWithThread extends SmsCommunicationBase {
  id: number;
  thread: SmsMessage[];
  contact?: ContactBaseModel;
  specific_identifier?: string;
  adjuster?: string;
  adjuster_role?: string;
}

// Define the Formik values interface
interface SmsFormValues {
  message_content: string;
}

// Define the component props interface
interface SmsContentTextFieldFormikProps {
  communication?: SmsCommunicationWithThread;
  onUpdate?: (communication: SmsCommunicationWithThread) => void;
  isView?: boolean;
  isNew?: boolean;
}

const SmsContentTextFieldFormik: React.FC<SmsContentTextFieldFormikProps> = (props) => {
  const { isView, isNew, communication, onUpdate } = props;
  const { values } = useFormikContext<SmsFormValues>();
  const messagesDivRef = React.useRef<HTMLDivElement>(null);
  const currentMessageDivRef = React.useRef<HTMLDivElement>(null);
  const maxAttachmentsHeight = '200px';
  const shouldShowSmsThread = !!(isView && communication && communication.thread);
  const characterLimit = getSmsCharacterLimit(values.message_content);

  React.useEffect(() => {
    if (shouldShowSmsThread && messagesDivRef.current && currentMessageDivRef.current) {
      messagesDivRef.current.scrollTo({
        top:
          currentMessageDivRef.current.offsetTop -
          messagesDivRef.current.clientHeight / 2 +
          currentMessageDivRef.current.clientHeight,
      });
      currentMessageDivRef.current.classList.add('bg-yellow-200');
    }
  }, [shouldShowSmsThread]);

  let communicationAttachments: CommunicationAttachment[] = [];
  if (isView && communication) {
    communicationAttachments = communication.thread.reduce(
      (acc: CommunicationAttachment[], message: SmsMessage) => [...acc, ...message.attachments],
      []
    );
  }

  return (
    <div className={shouldShowSmsThread ? 'flex h-[calc(100vh-100px)] w-full flex-col' : 'flex-1 p-4'}>
      {isNew && (
        <TextFieldFormik
          id={SMS_FORM_FORMIK_IDS.MESSAGE_CONTENT}
          label="Message"
          className="w-full"
          fullWidth
          multiline
          rows={5}
          inputProps={{ maxLength: characterLimit }}
          showOnly={isView}
        />
      )}

      {shouldShowSmsThread ? (
        <PanelGroup direction="vertical" id="sms-communication-panel-group" autoSaveId="sms-communication-panel-group">
          <Panel id="sms-thread" defaultSize={80} style={{ overflow: 'hidden' }}>
            <div ref={messagesDivRef} className="relative my-16 h-full overflow-auto">
              {communication.thread.map((message: SmsMessage) => {
                const contactIdentifier = isEmpty(communication!.contact)
                  ? communication!.specific_identifier
                  : communication!.contact!.full_name;
                const writer = message.direction === 'Incoming' ? contactIdentifier : communication!.adjuster;
                const writerRole =
                  message.direction === 'Incoming'
                    ? capitalize(communication!.contact?.role || '')
                    : capitalize(communication!.adjuster_role || '');
                const bgColor =
                  message.id !== communication!.id
                    ? message.direction === 'Incoming'
                      ? 'bg-blue-100'
                      : 'bg-slate-200'
                    : 'bg-yellow-100';
                const avatarColor = message.direction === 'Incoming' ? 'bg-orange-700' : 'bg-blue-600';
                const notRetrievedMediaCount = getNotRetrievedMediaCount(message);

                return (
                  <div key={message.id} ref={message.id === communication!.id ? currentMessageDivRef : undefined}>
                    <div className="flex items-start gap-4">
                      {message.direction === 'Incoming' ? (
                        <Avatar className={`h-24 w-24 p-16 font-medium ${avatarColor}`}>
                          {writer ? writer?.[0] : ''}
                        </Avatar>
                      ) : (
                        <div className="h-24 w-24" />
                      )}
                      <div className={`flex flex-1 flex-col items-start rounded-lg ${bgColor} mx-4 mb-16`}>
                        <div className="p-8">
                          <div className="flex items-center gap-4">
                            <Text variant={Text.VARIANTS.SM}>{writer || ''}</Text>
                            <Text
                              variant={Text.VARIANTS.XS}
                              colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                              weight={Text.WEIGHTS.REGULAR}
                            >
                              {writerRole}
                            </Text>
                          </div>
                          <Text
                            variant={Text.VARIANTS.SM}
                            weight={Text.WEIGHTS.REGULAR}
                            className="block whitespace-pre-wrap"
                          >
                            {message.message}
                          </Text>
                          {message.is_attachment_exist &&
                            message.attachments.map((attachment: CommunicationAttachment) => (
                              <InlineIconButton
                                key={attachment.id}
                                icon={MmsIcon}
                                tooltipTitle="View attachment"
                                onClick={() => window.open(getAttachmentFileUrl(attachment), '_target')}
                              />
                            ))}
                          {notRetrievedMediaCount > 0 && (
                            <Text
                              variant={Text.VARIANTS.XS}
                              colorVariant={Text.COLOR_VARIANTS.ERROR}
                              weight={Text.WEIGHTS.REGULAR}
                              className="block"
                            >
                              {notRetrievedMediaLabel(notRetrievedMediaCount)}
                            </Text>
                          )}
                          <div className="flex items-center justify-end gap-2">
                            <Text
                              variant={Text.VARIANTS.XS}
                              colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                              weight={Text.WEIGHTS.REGULAR}
                            >
                              {`${serverDateTimeToLocal(message.datetime)}`}
                            </Text>
                          </div>
                        </div>
                      </div>

                      {message.direction === 'Outgoing' ? (
                        <Avatar className={`h-24 w-24 p-16 font-medium ${avatarColor}`}>
                          {writer ? writer?.[0] : ''}
                        </Avatar>
                      ) : (
                        <div className="h-24 w-24" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          <PanelResizeHandle id="sms-resize-handle" className="my-12 h-2 w-full rounded-lg bg-slate-500" />

          <Panel id="sms-message-content" defaultSize={20} style={{ overflow: 'auto' }}>
            <div>
              {!isView && (
                <Text variant={Text.VARIANTS.XS} className="block">
                  {`${values.message_content.length}/${characterLimit}`}
                </Text>
              )}
              {isView && communication && communicationAttachments.length > 0 && onUpdate && (
                <CommunicationAttachmentTable
                  communication={{ ...communication, attachments: communicationAttachments }}
                  communicationType="sms"
                  onUpdate={onUpdate}
                  maxHeight={maxAttachmentsHeight}
                  getAttachmentFileUrl={getAttachmentFileUrl}
                />
              )}
            </div>
          </Panel>
        </PanelGroup>
      ) : (
        <div className="flex-1 p-4">
          {!isView && (
            <Text variant={Text.VARIANTS.XS} className="mb-4 block">
              {`${values.message_content.length}/${characterLimit}`}
            </Text>
          )}
          {isNew && <CommunicationReminderContainer />}
        </div>
      )}
    </div>
  );
};

export default SmsContentTextFieldFormik;
